import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  forwardRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { HtmlEditorComponent } from '../html-editor/html-editor.component';
import {
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { templateServices } from 'src/app/services/template.service';
import { firstValueFrom, Subject, debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { advanceSearchService } from 'src/app/services/advance-search';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SettingsDialogComponent } from '../default-template-fall/default-template-fall.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

@Component({
  selector: 'app-email',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HtmlEditorComponent,
    PickerComponent,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatDividerModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: './email.component.html',
  styleUrl: './email.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmailComponent),
      multi: true,
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EmailComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() tagsChanged: string = '';
  @Input() placeholder: string = 'Your input';
  @Input() title: string = 'Message';
  @Input()
  set signature(value: string) {
    this._signature = value;
  }

  @Input() formControl!: FormControl;
  @Input() allowConvert: boolean = false;
  @Input() height: string = '150px';
  @Input() isSendingMessage: boolean = false;

  @Input() lead: any;
  leadTags: any = [];
  _signature: string = '';
  templates: any = [];
  recent: any = [];
  otherFields: any = [];
  value: any = '';
  valueChecked: any = { newValue: '' };
  currentTemplate: any = { newValue: '' };
  allFields:any;
  @Output() templateChosen = new EventEmitter<any>();
  @Output() settingsClicked = new EventEmitter<any>();

  // UI state for emoji picker
  showEmojiPicker: boolean = false;

  // Pagination and search state
  templateSearchControl = new FormControl('');
  templateSearchSubject = new Subject<string>();
  templatePageIndex = 1;
  templatePageSize = 10;
  templateTotal = 0;
  isLoadingTemplates = false;
  isLoadingMoreTemplates = false;
  hasMoreTemplates = true;

  // Translation state
  isTranslating: boolean = false;
  isTranslated: boolean = false;
  private translationStateSubscription?: Subscription;
  private formValueSubscription?: Subscription;

  @ViewChild('templateMenuTrigger', { read: MatMenuTrigger }) templateMenuTrigger!: MatMenuTrigger;
  @ViewChild('templateMenuContent') templateMenuContent!: ElementRef;
  @ViewChild('templateSearchInput') templateSearchInput!: ElementRef;
  @ViewChild('htmlEditor', { read: HtmlEditorComponent }) htmlEditor!: HtmlEditorComponent;

  constructor(
    private templateService: templateServices,
    private advanceSearch: advanceSearchService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadTags();
    
    // Only load templates if isSendingMessage is true
    if (this.isSendingMessage) {
      this.loadTemplates();
      this.setupTemplateSearch();
    }
    
    // Listen to form control changes to sync translation state
    if (this.formControl) {
      this.formValueSubscription = this.formControl.valueChanges.subscribe(() => {
        // Small delay to let html-editor process the change first
        setTimeout(() => {
          if (this.htmlEditor && !this.htmlEditor.getCurrentTranslationState()) {
            this.isTranslated = false;
          }
        }, 50);
      });
    }
  }

  ngAfterViewInit(): void {
    // Listen to translation state changes from html editor
    if (this.htmlEditor) {
      this.translationStateSubscription = this.htmlEditor.translationStateChanged.subscribe((isTranslated: boolean) => {
        this.isTranslated = isTranslated;
      });
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.translationStateSubscription) {
      this.translationStateSubscription.unsubscribe();
    }
    if (this.formValueSubscription) {
      this.formValueSubscription.unsubscribe();
    }
  }

  // Handle translate button click - delegates to html editor
  async handleTranslate() {
    if (this.htmlEditor) {
      try {
        // Call the html editor's translate method and wait for the result
        const result = await this.htmlEditor.handleTranslate();
        
        // The state will be updated via the translationStateChanged event
        // so we don't need to manually update isTranslated here
        
      } catch (error) {
        console.error('Translation error:', error);
        // Keep the current state on error
      }
    }
  }

  setupTemplateSearch() {
    // Setup debounced search
    this.templateSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(searchValue => {
        this.templatePageIndex = 1;
        // Don't clear templates array to prevent re-render
        this.loadTemplates();
      });
  }

  onTemplateSearchChange(event: Event) {
    const searchValue = (event.target as HTMLInputElement).value;
    this.templateSearchSubject.next(searchValue);
  }

  onTemplateMenuScroll(event: Event) {
    const element = event.target as HTMLElement;
    const threshold = 0.8; // Load more when 80% scrolled
    
    if (element.scrollTop + element.clientHeight >= element.scrollHeight * threshold) {
      if (!this.isLoadingMoreTemplates && this.hasMoreTemplates) {
        this.loadMoreTemplates();
      }
    }
  }

  async loadMoreTemplates() {
    if (this.isLoadingMoreTemplates || !this.hasMoreTemplates) return;
    
    this.isLoadingMoreTemplates = true;
    this.templatePageIndex++;
    
    try {
      await this.loadTemplates(true);
    } finally {
      this.isLoadingMoreTemplates = false;
    }
  }

  async loadTemplates(append: boolean = false) {
    if (!append) {
      this.isLoadingTemplates = true;
    }

    let params: any = {
      type: 'email',
      isSendingMessage: true,
      pageIndex: this.templatePageIndex,
      pageSize: this.templatePageSize,
      sortField: '',
      sortDirection: '',
      searchValue: this.templateSearchControl.value || '',
      showArchive: 'false'  // Keep as string since backend expects string
    };

    if (this.lead && this.lead?.program) {
      params.program_id = this.lead.program.id;
    }
    
    try {
      const response = await firstValueFrom(
        this.templateService.get(params),
      );
      
      // Temporary fix: limit to pageSize even if backend returns more
      const limitedData = response.data.slice(0, this.templatePageSize);
      
      if (append) {
        this.templates = [...this.templates, ...limitedData];
      } else {
        this.templates = limitedData;
      }
      
      this.templateTotal = response.total;
      this.hasMoreTemplates = this.templates.length < this.templateTotal;
      
      // Restore focus to search input after loading
      this.focusSearchInput();
    } finally {
      this.isLoadingTemplates = false;
    }
  }

  onTemplateMenuOpened() {
    // Reset pagination when menu opens
    if (this.isSendingMessage) {
      this.templatePageIndex = 1;
      this.templateSearchControl.setValue('');
      this.loadTemplates();
      
      // Focus search input when menu opens
      setTimeout(() => {
        this.focusSearchInput();
      }, 100);
    }
  }

  focusSearchInput() {
    if (this.templateSearchInput && this.templateSearchInput.nativeElement) {
      setTimeout(() => {
        this.templateSearchInput.nativeElement.focus();
      }, 0);
    }
  }

  async onCreateTemplate(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    
    // Close the menu
    this.templateMenuTrigger?.closeMenu();
    
    // Dynamic import to avoid circular dependency
    const { AddEditTemplatesComponent } = await import('src/app/admissions/templates/add-edit-templates/add-edit-templates.component');
    
    const dialogRef = this.dialog.open(AddEditTemplatesComponent, {
      width: '90%',
      maxWidth: '1200px',
      height: '90%',
      maxHeight: '90vh',
      data: {
        isModal: true,
        defaultType: 'email' // Set default type to email since we're in email component
      },
      panelClass: 'template-edit-dialog'
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      // Expect { id: "8", action: "created" }
      if (!result?.id) return;
    
      this.templatePageIndex = 1;
      await this.loadTemplates(); // make sure it’s loaded
    
      const created = this.templates.find((t: any) => String(t.id) === String(result.id));
      if (created) {
        this.onTemplateSelect(created); // <-- this applies template immediately
      }
    
      // optional: reopen menu
      setTimeout(() => this.templateMenuTrigger?.openMenu(), 300);
    });
  }

  async onEditTemplate(event: Event, template: any) {
    event.stopPropagation();
    event.preventDefault();
    
    // Dynamic import to avoid circular dependency
    const { AddEditTemplatesComponent } = await import('src/app/admissions/templates/add-edit-templates/add-edit-templates.component');
    
    const dialogRef = this.dialog.open(AddEditTemplatesComponent, {
      width: '90%',
      maxWidth: '1200px',
      height: '90%',
      maxHeight: '90vh',
      data: {
        templateId: template.id,
        isModal: true
      },
      panelClass: 'template-edit-dialog'
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      // Expect { id: "8", action: "updated"|"created", type?: "email" }
      if (!result?.id) return;
    
      this.templatePageIndex = 1;
      await this.loadTemplates();
    
      const updated = this.templates.find((t: any) => String(t.id) === String(result.id));
      if (updated) {
        this.onTemplateSelect(updated);
      }
    
      // optional: reopen menu if you want
      setTimeout(() => this.templateMenuTrigger?.openMenu(), 300);
    });
  }

  // Only needed for emoji picker which is still using custom dropdown
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.emoji-picker-container') &&
      !target.closest('.emoji-btn')) {
      this.showEmojiPicker = false;
    }
  }

  toggleEmojiPicker(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmoji(event: any): void {
    const emoji = event.emoji.native;
    this.valueChecked = { newValue: emoji };
    this.showEmojiPicker = false;
  }

  stopEvent(event: any) {
    if (!event.emoji) {
      event.stopPropagation();
    }
  }

  async onSettingsClick(event: Event, item: any, menuTrigger?: MatMenuTrigger) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(SettingsDialogComponent, {
      width: '500px',
      data: {
        title: `Default value for ${item.label}`,
        defaultValue: item?.advanceSearchTypeSettings?.default
      }
    });

    // Handle the dialog close event
    dialogRef.afterClosed().subscribe(async result => {
      if (result !== undefined) {
        const response = await firstValueFrom(
          this.advanceSearch.createDefaultValue({
            defaultValue: result?.defaultValue,
            advance_search_type_id: item.id
          })
        );

        if (response) {
          // Update in otherFields array
          const index = this.otherFields.findIndex((field: any) => field.id === item.id);

          if (index !== -1) {
            this.otherFields[index] = {
              ...this.otherFields[index],
              advanceSearchTypeSettings: {
                ...this.otherFields[index].advanceSearchTypeSettings,
                default: result?.defaultValue
              }
            };

            // Force change detection
            this.otherFields = [...this.otherFields];
          }

          const leadIndex = this.leadTags.findIndex((field: any) => field.id === item.id);
          
          if (leadIndex !== -1) {
            this.leadTags[leadIndex] = {
              ...this.leadTags[leadIndex],
              advanceSearchTypeSettings: {
                ...this.leadTags[leadIndex].advanceSearchTypeSettings,
                default: result?.defaultValue
              }
            };
            this.leadTags = [...this.leadTags];
          }
        }

        menuTrigger?.closeMenu();
      }
    });
  }

  async onTagSelect(value: any) {

    if (this.isSendingMessage && this.allowConvert) {
      const rawValue = value.entitySearch
        ? this.lead[value.relationField]?.[value.entitySearch] ?? value?.advanceSearchTypeSettings?.default ?? 'null'
        : this.lead[value.field] ?? value?.advanceSearchTypeSettings?.default ?? 'null';
  
      const formattedValue = this.formatDateIfNeeded(rawValue);
      const convertValue = `${formattedValue}`;
      this.valueChecked = { newValue: convertValue };
    } else {
      const entitySuffix = value.entitySearch ? `.${value.entitySearch}` : '';
      const convertValue = `[[${value.entitySearch ? value.relationField : value.field}${entitySuffix}]]`;
      this.valueChecked = { newValue: convertValue };
    }
  
    const findExist = this.recent.find((x: any) => x.id === value.id);
    if (!findExist) {
      const response = await firstValueFrom(this.advanceSearch.lastUsedUpdate({}, value.id));
      if (response) {
        if (this.recent.length == 5) {
          this.recent.pop();
          this.recent.unshift(value);
        } else {
          this.recent.unshift(value);
        }
      }
    }
  }

  async loadTags() {
    const response = await firstValueFrom(
      this.templateService.get({}, 'fields'),
    );
    // Initialize arrays
    this.leadTags = [];
    this.otherFields = [];
    this.recent = response?.recent;
    this.allFields = response?.all
    // Populate leadTags and campusTagss based on 'entity'
    response.all.forEach((item: any) => {
      if (item.entity === 'leads' && !item.entitySearch) {
        this.leadTags.push(item);
      } else {
        this.otherFields.push(item);
      }
    });
  }

  onTemplateSelect(event: any) {
    // Reset translation state when selecting a new template
    this.isTranslated = false;
    
    if (event.attachments?.length > 0) {
      const eventCopy = JSON.parse(JSON.stringify(event));
      this.templateChosen.emit(eventCopy);
    } else {
      this.templateChosen.emit(event)
    }
    if (this.allowConvert && this.isSendingMessage) {
      const response = this.replacePlaceholders(event.message, this.lead);
      this.currentTemplate = { newValue: response };
    } else {
      this.currentTemplate = { newValue: event.message };
    }
  }

  onChange = (value: any) => { };
  onTouched = () => { };

  // Register change callback
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Register touched callback
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Write value to the component
  writeValue(value: any): void {
    this.value = value;
  }

  replacePlaceholders(template: string, data: any): string {
    const allFields = this.allFields || [];
  
    return template.replace(/\[\[([a-zA-Z0-9_.]+)\]\]/g, (_, key) => {
      const segments = key.split('.');
      let value: any = data;
      let missing = false;
  
      // Try to get the value from the data object
      for (const seg of segments) {
        if (value != null && typeof value === 'object' && seg in value) {
          value = value[seg];
        } else {
          missing = true;
          break;
        }
      }
  
      // If we found a value, format and return it
      if (!missing && value != null) {
        const formattedValue = this.formatDateIfNeeded(String(value));
        return formattedValue;
      }
  
      // If no value found, look for default value in field configuration
      let field = null;
      
      if (segments.length === 1) {
        // Simple field case (e.g., "firstname")
        field = allFields.find(f => f.field === key && !f.entitySearch);
      } else if (segments.length === 2) {
        // Nested field case (e.g., "campus.name")
        const [relationField, entitySearch] = segments;
        field = allFields.find(f => 
          f.relationField === relationField && 
          f.entitySearch === entitySearch
        );
      }
  
      // Return default value if found, otherwise return '[[xxx]]'
      if (field?.advanceSearchTypeSettings?.default != null) {
        const defaultValue = String(field.advanceSearchTypeSettings.default);
        const formattedDefault = this.formatDateIfNeeded(defaultValue);
        return formattedDefault;
      }
  
      return '[[xxx]]';
    });
  }

  private formatDateIfNeeded(value: any): any {
    if (typeof value !== 'string') return value;
    
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;
    if (isoDateRegex.test(value)) {
      const date = new Date(value);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }
    return value;
  }
}